/**
 * Local calendar-day helpers.
 *
 * `toISOString()` is UTC, so using it to derive a 'YYYY-MM-DD' key from a Date
 * built with `new Date(y, m, d)` (which is LOCAL midnight) silently shifts the
 * day for most of the world: in IST, local midnight on the 14th is 18:30 UTC on
 * the 13th, so the key comes back a day early and records land on the wrong
 * calendar cell. Anything that turns a Date into a day string for display,
 * lookup, or an <input type="date"> must go through here.
 */

/** Local 'YYYY-MM-DD' for a Date — never toISOString, which is UTC. */
export const localDateStr = (d = new Date()) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Today as a local 'YYYY-MM-DD' — for `max` on a date input, "is today", etc. */
export const todayStr = () => localDateStr(new Date());

/** Local 'YYYY-MM' for a Date — the month key the attendance APIs take. */
export const localMonthStr = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

/** Local 'YYYY-MM-DD' n days from now (negative for the past). */
export const daysFromTodayStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return localDateStr(d);
};

/** Is this a well-formed 'YYYY-MM-DD'? */
export const isIsoDay = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);

/**
 * Any DATE/DATETIME the API returns, as a bare 'YYYY-MM-DD', or null.
 *
 * Slicing the string is the whole point: `new Date('2026-09-11')` is UTC
 * midnight, so deriving a day from it lands on the 10th anywhere behind UTC.
 * Compare and store days as strings — 'YYYY-MM-DD' sorts and compares
 * correctly with `<`, `>` and `===`, in every zone, with no Date in the loop.
 */
export const apiDayStr = (v) => {
  if (!v) return null;
  const s = v instanceof Date ? localDateStr(v) : String(v).slice(0, 10);
  return isIsoDay(s) && !s.startsWith("0000") ? s : null;
};

/** 'YYYY-MM-DD' n days on from a day string. Stepped through UTC so no local
 *  offset or DST change can move the boundary. */
export const addDaysStr = (isoDay, n) => {
  if (!isIsoDay(isoDay)) return isoDay;
  const d = new Date(`${isoDay}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** '2026-09-07' → 'Mon Sep 07 2026'. Formatted from the string's own parts, so
 *  it can never render the previous day the way `new Date(s).toDateString()`
 *  does behind UTC. */
export const formatDayLong = (isoDay) => {
  if (!isIsoDay(isoDay)) return String(isoDay ?? "");
  const dow = DOW[new Date(`${isoDay}T00:00:00Z`).getUTCDay()];
  return `${dow} ${MON[Number(isoDay.slice(5, 7)) - 1]} ${isoDay.slice(8, 10)} ${isoDay.slice(0, 4)}`;
};
